import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { api } from '../services/api';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { useMasking } from './MaskingProvider.jsx';
import { MaskedCurrency } from './MaskedValue';
import FigureNote from './dashboard/FigureNote';
import { subscribeTangibleRefresh } from '../utils/tangibleRefresh';

/**
 * Top-bar metric blocks (v4 board, section 7.1; figures renamed by #2140,
 * epic #2138): Savings, Live Net Worth and Pending Income (when above zero)
 * from `/tangible`. Desktop: inline baseline rows 36px apart. Below md on a
 * list route the bar's second row holds the three in a three-column grid,
 * label over value. Detail routes (`detailOnly`) show Live Net Worth alone
 * at any width, so the 44px sticky-hero offset still matches the bar.
 *
 *   Savings        = savingsLedgerTotalCents
 *   Live Net Worth = accountBalanceCents + savingsLedgerTotalCents
 *                    (every ledger added together; positive budgets are NOT
 *                    subtracted, so this is not the API's netWorthCents)
 *   Pending Income = pendingIncomeTotalCents, shown above zero
 *
 * Each label-and-value pair carries its note (DESIGN_SYSTEM.md 8.20). The
 * `header-tangible` tour id stays on the Live Net Worth pair so guide anchors
 * resolve.
 */
const TangibleDisplay = ({ detailOnly = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const strings = useEngineStrings();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const stacked = isNarrow && !detailOnly;
  const { activeGroupId } = useGroup();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isMasked, toggleMasking } = useMasking();
  // A touch tap on a figure pair opens its note and leaves masking alone;
  // a tap on bar space still toggles masking (#2140 settle point 4).
  const tapOnPair = useRef(false);

  useEffect(() => {
    if (!activeGroupId) return;
    let cancelled = false;
    const load = async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true);
        setError(null);
        const res = await api.get(activeGroupId, '/tangible');
        if (!cancelled) setData(res || {});
      } catch (err) {
        console.error('Failed to load tangible value:', err);
        if (!cancelled) setError('Failed to calculate tangible value');
      } finally {
        if (!cancelled && !silent) setLoading(false);
      }
    };
    load();
    // Line mutations elsewhere in the app signal this event so the header
    // metrics track reality mid-session (silent: keep current values up
    // while the refetch runs instead of flashing the loading dash).
    // /tangible reads the stream-delta balance cache (#857), so the reloads
    // follow the shared backoff (#1104).
    const unsubscribe = subscribeTangibleRefresh(() => load({ silent: true }));
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [activeGroupId]);

  // v4 board metric blocks: label 10px, bar-label tracking (.08em), uppercase;
  // value 13px medium, sign-colored per section 11 off palette.bar.* (both
  // themes carry explicit bar tokens as of #1377). Blocks sit 36px apart on
  // desktop; stacked below md they fill three equal columns.
  const wrapSx = {
    cursor: 'pointer',
    userSelect: 'none',
    display: stacked ? 'grid' : 'flex',
    ...(stacked
      ? { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', columnGap: '12px', width: '100%' }
      : { flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap', gap: { xs: 2, md: '36px' } }),
    lineHeight: 1.1,
    '&:hover': { opacity: 0.8 },
  };

  const labelSx = {
    fontSize: '10px',
    fontWeight: theme.ds.font.weightMedium,
    color: theme.palette.bar.label,
    textTransform: 'uppercase',
    letterSpacing: theme.ds.font.letterBarLabel,
    lineHeight: 1.2,
  };

  const valueBaseSx = {
    fontSize: `${theme.ds.font.value}px`,
    fontWeight: theme.ds.font.weightMedium,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
  };

  // One figure: the label-and-value pair is the note's target and, for
  // Pending Income, the link itself, so one element carries both.
  const renderChip = (key, valueCents, onClick, tourId) => {
    const label = strings[`header.${key}`];
    const clickable = typeof onClick === 'function';
    const cents = valueCents || 0;
    const { bar } = theme.palette;
    const color = cents < 0 ? bar.negative : cents === 0 ? bar.zero : bar.positive;
    return (
      <FigureNote
        key={key}
        note={strings[`header.${key}.note`]}
        data-tour-id={tourId}
        data-testid={`header-${key}`}
        onClick={clickable ? onClick : undefined}
        role={clickable ? 'button' : undefined}
        onKeyDown={clickable ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(e);
          }
        } : undefined}
        title={clickable ? strings['header.pendingIncome.link'] : undefined}
        sx={{
          // Kit BrdMetric (#1405): label and value share one baseline row.
          // Inline keeps the bar content short so the fixed 44px bar holds.
          display: 'flex',
          flexDirection: stacked ? 'column' : 'row',
          alignItems: stacked ? 'flex-start' : 'baseline',
          gap: stacked ? '2px' : '9px',
          ...(clickable ? { cursor: 'pointer' } : {}),
        }}
      >
        <Typography component="span" sx={labelSx}>
          {label}
        </Typography>
        <Typography component="span" sx={{ ...valueBaseSx, color }}>
          <MaskedCurrency value={cents / 100} />
        </Typography>
      </FigureNote>
    );
  };

  // Clicking "Pending Income" opens the Budgets page, where the Pending
  // Income card is pinned at the top (#906, no scroll since #2179).
  // stopPropagation keeps it from also toggling masking on the wrapping Box.
  const goToPendingIncome = (e) => {
    e.stopPropagation();
    navigate('/budgets');
  };

  const placeholder = (text) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '9px' }}>
      <Typography component="span" sx={labelSx}>{strings['header.liveNetWorth']}</Typography>
      <Typography component="span" sx={{ ...valueBaseSx, color: theme.palette.bar.label }}>{text}</Typography>
    </Box>
  );

  let body;
  if (loading) {
    body = placeholder('-');
  } else if (error) {
    body = placeholder('Error');
  } else {
    const savingsCents = data?.savingsLedgerTotalCents ?? 0;
    const liveNetWorthCents = (data?.accountBalanceCents ?? 0) + savingsCents;
    const pendingIncomeCents = data?.pendingIncomeTotalCents ?? 0;
    const liveNetWorth = renderChip('liveNetWorth', liveNetWorthCents, undefined, 'header-tangible');
    if (detailOnly) {
      body = liveNetWorth;
    } else {
      const pendingIncome = pendingIncomeCents > 0
        ? renderChip('pendingIncome', pendingIncomeCents, goToPendingIncome, 'header-pending-income')
        : (stacked ? <Box key="pendingIncome" data-testid="header-pendingIncome-empty" /> : null);
      body = (
        <>
          {renderChip('savings', savingsCents)}
          {liveNetWorth}
          {pendingIncome}
        </>
      );
    }
  }

  return (
    <Box
      data-testid="header-metrics"
      data-stacked={stacked ? 'true' : 'false'}
      onPointerDownCapture={(e) => {
        tapOnPair.current = e.pointerType === 'touch' && Boolean(e.target.closest('[data-figure-note]'));
      }}
      onClick={() => {
        if (tapOnPair.current) {
          tapOnPair.current = false;
          return;
        }
        toggleMasking();
      }}
      sx={wrapSx}
      title={isMasked ? 'Click to show real values' : 'Click to mask sensitive data'}
    >
      {body}
    </Box>
  );
};

export default TangibleDisplay;
